// src/pages/AdminDepositPage.js
import React, { useState, useEffect } from "react";
import "../styles/news.css"; // consider renaming → admin-deposit.css

function AdminDepositPage() {
  const [users, setUsers]                 = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading]         = useState(true);
  const [error, setError]                 = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [searchQuery, setSearchQuery]     = useState("");
  const [selectedUser, setSelectedUser]   = useState(null);
  const [depositAmount, setDepositAmount] = useState("");

  // Very useful during debugging
  const apiBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
  const depositEndpoint = "/transactions/deposit";   // ← easy to change when needed

  useEffect(() => {
    const fetchBalances = async () => {
      try {
        const url = `${apiBase}/wallet/balances`;
        console.log("[FETCH] Getting balances from:", url);

        const res = await fetch(url);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Balances fetch failed ${res.status} – ${text}`);
        }

        const data = await res.json();
        setUsers(data);
        setFilteredUsers(data);
      } catch (err) {
        console.error("Balances fetch error:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalances();
  }, [apiBase]);

  useEffect(() => {
    const q = searchQuery.toLowerCase().trim();
    const filtered = users.filter(
      u =>
        (u?.username || "").toLowerCase().includes(q) ||
        (u?.email    || "").toLowerCase().includes(q)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const selectUser = (user) => {
    setSelectedUser(user);
    setDepositAmount("");
    setError(null);
    setSuccessMessage(null);
  };

  const handleDeposit = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!selectedUser) {
      setError("Select a user first");
      return;
    }

    const amt = Number(depositAmount);
    if (!depositAmount || isNaN(amt) || amt <= 0) {
      setError("Enter a valid positive amount");
      return;
    }

    const payload = {
      username: selectedUser.username,
      amount: amt,
    };

    try {
      const url = `${apiBase}${depositEndpoint}`;
      console.log("[DEPOSIT] →", url);
      console.log("Payload:", payload);

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // If you have token-based auth:
          // "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error(`Deposit failed → ${res.status}`, text);
        throw new Error(`Server error: ${res.status} – ${text || "no message"}`);
      }

      const result = await res.json();
      console.log("Deposit success:", result);

      // Refresh balances
      const balRes = await fetch(`${apiBase}/wallet/balances`);
      if (balRes.ok) {
        const fresh = await balRes.json();
        setUsers(fresh);
        setFilteredUsers(fresh);
      }

      setSuccessMessage(`Successfully deposited ${amt} to ${selectedUser.username}`);
      setSelectedUser(null);
      setDepositAmount("");

    } catch (err) {
      console.error("Deposit flow failed:", err);
      setError(err.message || "Deposit failed – check console");
    }
  };

  if (isLoading) return <div className="loading">Loading balances...</div>;
  if (error)   return <div className="error">Error: {error}</div>;

  return (
    <div className="wallet-page">
      <div className="wallet-content">
        <h2>Admin – Deposit to User Wallet</h2>

        <div className="fixed-action-bar">
          <div className="input-button-group">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search username or email..."
              autoComplete="off"
              style={{ flex: 1 }}
            />

            <button
              className="deposit-button"
              onClick={handleDeposit}
              disabled={!selectedUser || !depositAmount.trim()}
            >
              Confirm Deposit
            </button>
          </div>
        </div>

        {error && (
          <div className="error-msg" style={{ color: "red", margin: "12px 0", fontWeight: 600 }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-msg" style={{ color: "green", margin: "12px 0", fontWeight: 600 }}>
            {successMessage}
          </div>
        )}

        <div className="wallet-form">
          <div className="form-group">
            <label>Selected User</label>
            <input
              type="text"
              value={selectedUser?.username || ""}
              readOnly
              placeholder="← select from table"
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label>Current Balance</label>
            <input
              type="text"
              value={
                selectedUser
                  ? `${selectedUser.balance ?? "0"} ${selectedUser.currency ?? "—"}`
                  : "—"
              }
              readOnly
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label>Deposit Amount</label>
            <input
              type="number"
              value={depositAmount}
              onChange={e => setDepositAmount(e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.00000001" // good for crypto
              autoComplete="off"
            />
          </div>
        </div>

        <div className="table-container">
          <table className="wallet-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Balance</th>
                <th>Currency</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr
                  key={user.id}
                  onClick={() => selectUser(user)}
                  className={selectedUser?.id === user.id ? "selected-row" : ""}
                >
                  <td>{user.username}</td>
                  <td>{user.email || "—"}</td>
                  <td>{user.balance ?? "0"}</td>
                  <td>{user.currency || "—"}</td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", padding: "20px" }}>
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminDepositPage;